import { Controller } from "@hotwired/stimulus"

// Multi-select for external reviewer emails.
// Shows a popover with saved company emails + "Add New Email" with MX validation.
// Dispatches "change" event with { emails: [...] } whenever selection changes.

export default class extends Controller {
  static targets = [
    "trigger", "popover", "search", "list", "chips",
    "addEmailRow", "addEmailInput", "addEmailError", "addEmailBtn"
  ]

  static values = {
    emails: Array,           // [{ email: "..." }] — saved company external emails
    selected: String,        // space-separated initial selection
    validateEmailUrl: String // /validate_email
  }

  connect() {
    this._selected = new Set()
    this._addingEmail = false

    if (this.selectedValue) {
      this.selectedValue.split(/\s+/).filter(Boolean).forEach(e => this._selected.add(e.toLowerCase()))
    }

    this._onClickOutside = (e) => {
      if (this.hasPopoverTarget && !this.element.contains(e.target)) {
        this._close()
      }
    }
    document.addEventListener("click", this._onClickOutside)

    this._renderChips()
  }

  disconnect() {
    document.removeEventListener("click", this._onClickOutside)
  }

  // ── Public API ──────────────────────────────────────────────────────

  get selectedEmails() {
    return Array.from(this._selected)
  }

  // Restore a saved draft's emails (no change event — nothing else needs to react).
  setSelection(emails) {
    this._selected = new Set((emails || []).filter(Boolean).map(e => String(e).toLowerCase()))
    this._renderChips()
  }

  // ── Popover ─────────────────────────────────────────────────────────

  toggle() {
    if (!this.hasPopoverTarget) return
    this.popoverTarget.classList.contains("hidden") ? this._open() : this._close()
  }

  _open() {
    this._hideAddEmail()
    this._renderList("")
    this.popoverTarget.classList.remove("hidden")
    if (this.hasSearchTarget) {
      this.searchTarget.value = ""
      this.searchTarget.focus()
    }
  }

  _close() {
    if (this.hasPopoverTarget) this.popoverTarget.classList.add("hidden")
    this._hideAddEmail()
  }

  filter() {
    const q = this.hasSearchTarget ? this.searchTarget.value.trim().toLowerCase() : ""
    this._renderList(q)
  }

  _renderList(query) {
    if (!this.hasListTarget) return

    const emails = (this.emailsValue || []).map(e => e.email || e)
    const filtered = emails.filter(e => {
      if (!query) return true
      return e.toLowerCase().includes(query)
    })

    let html = ""
    if (filtered.length === 0 && !query) {
      html = `<div class="px-3 py-3 text-[13px] text-gray-400 text-center">No saved emails</div>`
    } else if (filtered.length === 0) {
      html = `<div class="px-3 py-3 text-[13px] text-gray-400 text-center">No matches</div>`
    } else {
      html = filtered.map(email => {
        const checked = this._selected.has(email.toLowerCase())
        return `<div data-action="click->external-users-select#selectEmail"
                     data-email="${this._esc(email)}"
                     class="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 cursor-pointer transition-colors ${checked ? 'bg-brand/5' : ''}">
          <div class="w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${checked ? 'border-brand bg-brand' : 'border-gray-300'}">
            ${checked ? '<svg class="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke-width="3" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>' : ''}
          </div>
          <div class="text-sm text-gray-900 truncate">${this._esc(email)}</div>
        </div>`
      }).join("")
    }

    this.listTarget.innerHTML = html
  }

  // ── Selection ───────────────────────────────────────────────────────

  selectEmail(event) {
    const email = event.currentTarget.dataset.email.toLowerCase()
    if (this._selected.has(email)) {
      this._selected.delete(email)
    } else {
      this._selected.add(email)
    }
    const query = this.hasSearchTarget ? this.searchTarget.value.trim().toLowerCase() : ""
    this._renderList(query)
    this._renderChips()
    this._dispatchChange()
  }

  removeChip(event) {
    const email = event.currentTarget.dataset.email.toLowerCase()
    this._selected.delete(email)
    this._renderChips()
    this._dispatchChange()
  }

  // ── Add New Email ───────────────────────────────────────────────────

  showAddEmail() {
    if (!this.hasAddEmailRowTarget) return
    this._addingEmail = true
    this.addEmailRowTarget.classList.remove("hidden")
    if (this.hasAddEmailInputTarget) {
      this.addEmailInputTarget.value = ""
      this.addEmailInputTarget.classList.remove("border-red-400", "ring-1", "ring-red-400")
      setTimeout(() => this.addEmailInputTarget.focus(), 50)
    }
    if (this.hasAddEmailErrorTarget) {
      this.addEmailErrorTarget.classList.add("hidden")
    }
    if (this.hasAddEmailBtnTarget) {
      this.addEmailBtnTarget.disabled = false
      this.addEmailBtnTarget.textContent = "Add"
    }
  }

  _hideAddEmail() {
    this._addingEmail = false
    if (this.hasAddEmailRowTarget) {
      this.addEmailRowTarget.classList.add("hidden")
    }
  }

  addEmailKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.submitNewEmail()
    } else if (event.key === "Escape") {
      this._hideAddEmail()
    }
  }

  async submitNewEmail() {
    if (!this.hasAddEmailInputTarget) return

    const email = this.addEmailInputTarget.value.trim().toLowerCase()

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this._showAddError("Please enter a valid email address.")
      return
    }

    // Check if already selected
    if (this._selected.has(email)) {
      this._hideAddEmail()
      return
    }

    // Backend MX validation
    if (this.validateEmailUrlValue) {
      this._setAddLoading(true)
      try {
        const url = `${this.validateEmailUrlValue}?email=${encodeURIComponent(email)}`
        const resp = await fetch(url, { headers: { "Accept": "application/json" } })
        if (resp.ok) {
          const data = await resp.json()
          if (!data.is_valid) {
            this._setAddLoading(false)
            this._showAddError("This email address doesn't exist. Please check for typos.")
            return
          }
        }
      } catch { /* proceed */ }
      this._setAddLoading(false)
    }

    // Add to pool if not already there
    const pool = (this.emailsValue || []).map(e => (e.email || e).toLowerCase())
    if (!pool.includes(email)) {
      const updated = [...(this.emailsValue || []), { email }]
      this.emailsValue = updated
    }

    this._selected.add(email)
    this._hideAddEmail()
    this._renderList("")
    this._renderChips()
    this._dispatchChange()
  }

  _showAddError(msg) {
    if (!this.hasAddEmailErrorTarget) return
    this.addEmailErrorTarget.textContent = msg
    this.addEmailErrorTarget.classList.remove("hidden")
    this.addEmailInputTarget?.classList.add("border-red-400", "ring-1", "ring-red-400")
  }

  _setAddLoading(loading) {
    if (!this.hasAddEmailBtnTarget) return
    this.addEmailBtnTarget.disabled = loading
    this.addEmailBtnTarget.textContent = loading ? "Validating..." : "Add"
  }

  // ── Rendering ───────────────────────────────────────────────────────

  _renderChips() {
    if (!this.hasChipsTarget) return

    const chips = this.selectedEmails.map(email => {
      const label = email.split("@")[0]
      return `<span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[13px] font-medium border bg-gray-50 text-gray-700 border-gray-200" data-email="${this._esc(email)}">
        ${this._esc(label)}
        <button type="button" data-action="click->external-users-select#removeChip" data-email="${this._esc(email)}" class="text-gray-400 hover:text-gray-600 cursor-pointer">&times;</button>
      </span>`
    }).join("")

    this.chipsTarget.innerHTML = chips
  }

  _dispatchChange() {
    this.dispatch("change", { detail: { emails: this.selectedEmails } })
  }

  // ── Utilities ───────────────────────────────────────────────────────

  // Safe in BOTH text and attribute position — quotes must be escaped too,
  // or a value carrying an inch mark (`6" VERTICAL CURB`) closes its own
  // attribute and swallows the rest of the tag, data-* attributes included.
  _esc(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
